/**
 * 通用文件预览模态框组件
 * 复用管理页面 FileDetailsViewer 的预览逻辑
 * 支持：图片、视频、PDF、代码/Markdown、Office 文档
 */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { KnowledgeFile } from '../../types';
import { getSignedUrl, getPublicUrl } from '../../config/storage';
import { formatFileSize, getLanguageFromExtension, isMarkdownFile } from '../../utils/file-types';
import { cn } from '../../utils/cn';
import { 
  Download, X, FileText, File, 
  ChevronLeft, ChevronRight, Play
} from 'lucide-react';
import dayjs from 'dayjs';

// PDF 预览
import { Document, Page, pdfjs } from 'react-pdf';
// 视频播放
import ReactPlayer from 'react-player';
// 代码高亮和 Markdown 预览
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
// 水豚加载动画
import CapybaraLoader from './Progressbar';

// 设置 PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface FilePreviewModalProps {
  file: KnowledgeFile & { url?: string };
  isOpen: boolean;
  onClose: () => void;
  onDownload?: (file: KnowledgeFile) => void;
}

const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  file,
  isOpen,
  onClose,
  onDownload
}) => {
  // 各类型文件的 URL
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [officeViewerUrl, setOfficeViewerUrl] = useState<string | null>(null);
  const [codeContent, setCodeContent] = useState<string | null>(null);
  
  // 加载状态
  const [isLoading, setIsLoading] = useState(true);
  const [officePreviewError, setOfficePreviewError] = useState(false);
  
  // PDF 翻页
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  
  const videoRef = useRef<ReactPlayer>(null);

  // 判断是否是真正的 Office 文件（基于扩展名）
  const isOfficeFile = React.useMemo(() => {
    if (!file || !file.name) return false;
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    return ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext);
  }, [file?.name]);

  // 判断是否是可预览的文本文件（txt, log, md 等）
  const isTextFile = React.useMemo(() => {
    if (!file || !file.name) return false;
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    return ['txt', 'text', 'log', 'md', 'markdown', 'json', 'xml', 'yaml', 'yml', 'ini', 'conf', 'cfg', 'env'].includes(ext);
  }, [file?.name]);

  // 加载文件 URL
  useEffect(() => {
    if (!isOpen || !file || !file.storage_key) return;

    const loadUrls = async () => {
      setIsLoading(true);
      try {
        // 首先检查扩展名：如果是文本文件，直接加载文本内容（无论 file.type 是什么）
        if (isTextFile) {
          const txtUrl = await getSignedUrl(file.storage_key!);
          const response = await fetch(txtUrl);
          const text = await response.text();
          if (text.length > 500000) {
            setCodeContent(text.slice(0, 500000) + '\n\n... (文件过大，仅显示前 500KB)');
          } else {
            setCodeContent(text);
          }
          setIsLoading(false);
          return;
        }

        switch (file.type) {
          case 'image':
            const imgUrl = file.url || await getSignedUrl(file.storage_key!);
            setImageUrl(imgUrl);
            break;
            
          case 'pdf':
            const pUrl = file.url || await getSignedUrl(file.storage_key!);
            setPdfUrl(pUrl);
            break;
            
          case 'video':
            const vUrl = file.url || await getSignedUrl(file.storage_key!);
            setVideoUrl(vUrl);
            break;
            
          case 'document':
          case 'spreadsheet':
          case 'presentation':
            // 只有真正的 Office 文件才使用 Office Online Viewer
            if (isOfficeFile) {
              const fileUrl = await getPublicUrl(file.storage_key!);
              const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
              setOfficeViewerUrl(viewerUrl);
            }
            break;
            
          case 'code':
            const codeUrl = await getSignedUrl(file.storage_key!);
            const response = await fetch(codeUrl);
            const text = await response.text();
            if (text.length > 500000) {
              setCodeContent(text.slice(0, 500000) + '\n\n... (文件过大，仅显示前 500KB)');
            } else {
              setCodeContent(text);
            }
            break;
        }
      } catch (error) {
        console.error('加载文件预览失败:', error);
        setOfficePreviewError(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadUrls();
  }, [isOpen, file, isOfficeFile, isTextFile]);

  // 清理状态
  useEffect(() => {
    if (!isOpen) {
      setImageUrl(null);
      setPdfUrl(null);
      setVideoUrl(null);
      setOfficeViewerUrl(null);
      setCodeContent(null);
      setPageNumber(1);
      setNumPages(null);
      setIsLoading(true);
      setOfficePreviewError(false);
    }
  }, [isOpen]);

  // PDF 加载成功
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  // 处理下载
  const handleDownload = () => {
    if (onDownload) {
      onDownload(file);
    }
  };

  // 渲染预览内容
  const renderPreviewContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          {/* 水豚加载动画 */}
          <CapybaraLoader />
          
          {/* 文件名 */}
          <p className="text-base font-medium text-gray-900 dark:text-white mt-2 max-w-xs mx-auto truncate px-4">
            {file?.name || '文件'}
          </p>
          
          {/* 加载提示 */}
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            正在加载中...
          </p>
        </div>
      );
    }

    // 优先处理文本文件（txt, log, md 等）- 基于扩展名而非 file.type
    if (isTextFile && codeContent) {
      const language = getLanguageFromExtension(file.name);
      const isMarkdown = isMarkdownFile(file.name);
      
      // Markdown 使用浅色背景
      if (isMarkdown) {
        return (
          <div className="h-full bg-white dark:bg-gray-900 overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-4 py-2 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">{file.name}</span>
              <span className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded">markdown</span>
            </div>
            <div className="flex-1 overflow-auto p-6">
              <div className="prose prose-sm md:prose-base max-w-none dark:prose-invert prose-headings:text-gray-900 dark:prose-headings:text-white prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-strong:text-gray-900 dark:prose-strong:text-white prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-code:text-pink-600 dark:prose-code:text-pink-400 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-pre:bg-slate-900 prose-pre:text-slate-100 prose-table:border-collapse prose-th:bg-gray-100 dark:prose-th:bg-gray-800 prose-th:border prose-th:border-gray-300 dark:prose-th:border-gray-600 prose-th:px-3 prose-th:py-2 prose-td:border prose-td:border-gray-300 dark:prose-td:border-gray-600 prose-td:px-3 prose-td:py-2">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    code({ node, className, children, ...props }) {
                      const match = /language-(\w+)/.exec(className || '');
                      const inline = !match;
                      return !inline ? (
                        <SyntaxHighlighter style={oneDark} language={match ? match[1] : 'text'} PreTag="div">
                          {String(children).replace(/\n$/, '')}
                        </SyntaxHighlighter>
                      ) : (
                        <code className={className} {...props}>{children}</code>
                      );
                    }
                  }}
                >
                  {codeContent}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        );
      }
      
      // 普通文本文件使用浅色背景，易于阅读
      return (
        <div className="h-full bg-white dark:bg-gray-900 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-4 py-2 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">{file.name}</span>
            <span className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded">{language}</span>
          </div>
          <div className="flex-1 overflow-auto p-6">
            <pre className="text-sm text-gray-800 dark:text-gray-200 font-mono whitespace-pre-wrap break-words leading-relaxed">
              {codeContent}
            </pre>
          </div>
        </div>
      );
    }

    switch (file.type) {
      case 'image':
        return imageUrl ? (
          <div className="flex items-center justify-center h-full p-4">
            <img
              src={imageUrl}
              alt={file.name}
              className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
            />
          </div>
        ) : null;

      case 'pdf':
        return pdfUrl ? (
          <div className="flex flex-col h-full">
            <div className="flex-1 overflow-auto flex items-center justify-center bg-gray-100 dark:bg-gray-800">
              <Document
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={
                  <div className="flex items-center justify-center py-20">
                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                }
                className="flex items-center justify-center"
              >
                <Page 
                  pageNumber={pageNumber} 
                  renderTextLayer={false} 
                  renderAnnotationLayer={false} 
                  className="shadow-lg"
                  height={Math.min(600, window.innerHeight - 200)}
                />
              </Document>
            </div>
            {numPages && (
              <div className="flex items-center justify-center py-3 space-x-4 bg-gray-50 dark:bg-gray-900 border-t dark:border-gray-700">
                <button
                  onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                  disabled={pageNumber <= 1}
                  className="p-2 text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 rounded-lg shadow-sm disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow-sm">
                  {pageNumber} / {numPages}
                </span>
                <button
                  onClick={() => setPageNumber(p => Math.min(numPages, p + 1))}
                  disabled={pageNumber >= numPages}
                  className="p-2 text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 rounded-lg shadow-sm disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        ) : null;

      case 'video':
        return videoUrl ? (
          <div className="flex items-center justify-center h-full bg-black p-4">
            <div className="w-full max-w-4xl aspect-video">
              <ReactPlayer
                ref={videoRef}
                url={videoUrl}
                controls
                width="100%"
                height="100%"
                config={{
                  file: {
                    attributes: {
                      controlsList: 'nodownload',
                      crossOrigin: 'anonymous'
                    }
                  }
                }}
              />
            </div>
          </div>
        ) : null;

      case 'code':
        if (codeContent) {
          const language = getLanguageFromExtension(file.name);
          const isMarkdown = isMarkdownFile(file.name);
          
          // Markdown 使用浅色背景，便于阅读
          if (isMarkdown) {
            return (
              <div className="h-full bg-white dark:bg-gray-900 overflow-hidden flex flex-col">
                <div className="flex items-center justify-between px-4 py-2 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">{file.name}</span>
                  <span className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded">markdown</span>
                </div>
                <div className="flex-1 overflow-auto p-6">
                  <div className="prose prose-sm md:prose-base max-w-none dark:prose-invert prose-headings:text-gray-900 dark:prose-headings:text-white prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-strong:text-gray-900 dark:prose-strong:text-white prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-code:text-pink-600 dark:prose-code:text-pink-400 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-pre:bg-slate-900 prose-pre:text-slate-100 prose-table:border-collapse prose-th:bg-gray-100 dark:prose-th:bg-gray-800 prose-th:border prose-th:border-gray-300 dark:prose-th:border-gray-600 prose-th:px-3 prose-th:py-2 prose-td:border prose-td:border-gray-300 dark:prose-td:border-gray-600 prose-td:px-3 prose-td:py-2">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        code({ node, className, children, ...props }) {
                          const match = /language-(\w+)/.exec(className || '');
                          const inline = !match;
                          return !inline ? (
                            <SyntaxHighlighter style={oneDark} language={match ? match[1] : 'text'} PreTag="div">
                              {String(children).replace(/\n$/, '')}
                            </SyntaxHighlighter>
                          ) : (
                            <code className={className} {...props}>{children}</code>
                          );
                        }
                      }}
                    >
                      {codeContent}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            );
          }
          
          // 代码文件使用深色背景，便于语法高亮
          return (
            <div className="h-full bg-slate-900 overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-4 py-2 bg-slate-800 border-b border-slate-700">
                <span className="text-sm text-slate-300 font-mono">{file.name}</span>
                <span className="text-xs text-slate-500 bg-slate-700 px-2 py-0.5 rounded">{language}</span>
              </div>
              <div className="flex-1 overflow-auto">
                <SyntaxHighlighter
                  language={language}
                  style={oneDark}
                  showLineNumbers
                  wrapLines
                  customStyle={{ margin: 0, borderRadius: 0, minHeight: '100%' }}
                >
                  {codeContent}
                </SyntaxHighlighter>
              </div>
            </div>
          );
        }
        return renderDownloadPrompt('代码文件');

      case 'document':
      case 'spreadsheet':
      case 'presentation':
        if (isOfficeFile && officeViewerUrl && !officePreviewError) {
          return (
            <div className="w-full" style={{ minHeight: '70vh' }}>
              <iframe
                src={officeViewerUrl}
                className="w-full h-full border-none"
                style={{ minHeight: '70vh' }}
                title={`${file.name} 预览`}
                onError={() => setOfficePreviewError(true)}
              />
            </div>
          );
        }
        return renderDownloadPrompt('文档文件');

      default:
        return renderDownloadPrompt('文件');
    }
  };

  // 渲染下载提示
  const renderDownloadPrompt = (fileTypeLabel: string) => (
    <div className="flex flex-col items-center justify-center h-full py-16 text-center">
      <div className="w-20 h-20 rounded-2xl bg-slate-200 dark:bg-slate-700 flex items-center justify-center mb-4">
        <FileText className="w-10 h-10 text-blue-500" />
      </div>
      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
        {file.name}
      </h4>
      <p className="text-gray-500 dark:text-gray-400 mb-6">
        {fileTypeLabel}暂不支持在线预览，请下载后查看
      </p>
      <button
        onClick={handleDownload}
        className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl transition-colors flex items-center gap-2"
      >
        <Download className="w-5 h-5" />
        下载文件
      </button>
    </div>
  );

  if (!isOpen || !file) return null;

  return createPortal(
    <div 
      className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-md flex items-center justify-center p-4" 
      style={{ zIndex: 99999 }}
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-gray-900 backdrop-blur-xl rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden border border-gray-200 dark:border-white/10 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 头部 */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-black/30 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate pr-4">
            {file.name}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              className="p-2.5 bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-white/70 hover:bg-gray-200 dark:hover:bg-white/20 hover:text-gray-900 dark:hover:text-white rounded-xl transition-all"
              title="下载"
            >
              <Download className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2.5 bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-white/70 hover:bg-red-100 dark:hover:bg-red-500/20 hover:text-red-600 dark:hover:text-red-400 rounded-xl transition-all"
              title="关闭"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* 内容区 - 设置最小高度确保加载状态有足够空间 */}
        <div className="flex-1 overflow-auto bg-gray-100 dark:bg-black/20" style={{ minHeight: '60vh' }}>
          {renderPreviewContent()}
        </div>

        {/* 底部信息栏 */}
        <div className="px-4 py-3 bg-gray-50 dark:bg-black/30 border-t border-gray-200 dark:border-white/10 flex-shrink-0">
          <div className="flex items-center justify-between text-sm text-gray-600 dark:text-white/60">
            <span>大小: {formatFileSize(file.size)}</span>
            <span>创建时间: {dayjs(file.createdAt).format('YYYY年M月D日')}</span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FilePreviewModal;
